<script setup>
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import PrioritisedPageRow from "@/components/kit-settings/partials/PrioritisedPageRow.vue";
import NoPrioritisedPages from "@/components/kit-settings/partials/NoPrioritisedPages.vue";
import TabHeading from "@/components/kit-settings/partials/TabHeading.vue";
import {VueDraggable} from "vue-draggable-plus";
import {prioritisedPages, useKitPages} from "@/composables/kit-settings/useKitPages";
import {getKitSettings} from "@/composables/kit-settings/useKitSettings";
import FadeGroupTransition from "@/components/transitions/FadeGroupTransition.vue";

const {getPrioritisedPages, updatePagePriorities, recalculatePrioritisedPageIndexes} = useKitPages()
const {isPending} = getPrioritisedPages();
const {isPending: fetchingSettings} = getKitSettings();

const {mutate: update} = updatePagePriorities();

const onPrioritiesReordered = () => {
  recalculatePrioritisedPageIndexes();
  update();
};
</script>

<template>
  <div class="tw-w-full tw-flex tw-flex-col tw-gap-8 tw-border tw-border-neutral-300 tw-rounded-xl tw-bg-white tw-relative tw-p-8 tw-overflow-hidden">
    <div class="tw-flex tw-flex-col tw-gap-4">
      <div class="tw-flex tw-justify-between tw-items-center">
        <TabHeading
          class="tw-h-12"
          icon="icons/star"
          heading="Priority Pages"
        />

        <div class="tw-flex tw-gap-4 tw-items-center tw-text-neutral">
          <svg-vue
            class="tw-h-6"
            icon="icons/drag-and-drop"
          />

          <div class="tw-text-neutral-500">
            Drag and drop to reorder
          </div>
        </div>
      </div>

      <div class="tw-text-neutral-500">
        Below are the pages you’ve chosen to work on first. I’ll work on them in the order shown below. You can drag and drop to reorder, or click the star icon to remove a page from your priority list.
      </div>
    </div>

    <VueDraggable
      v-if="prioritisedPages?.length > 0 && !isPending && !fetchingSettings"
      v-model="prioritisedPages"
      :animation="1500"
      class="tw-flex tw-flex-col tw-gap-4 tw-overflow-auto"
      target=".priority-pages"
      :scroll="true"
      group="priority"
      @end="onPrioritiesReordered"
    >
      <FadeGroupTransition class="priority-pages tw-flex tw-flex-col tw-gap-2">
        <PrioritisedPageRow
          v-for="(page, index) in prioritisedPages"
          :key="page.id || index"
          :index="page.monthIndex"
          :page="page"
        />
      </FadeGroupTransition>
    </VueDraggable>

    <LoadingBlock v-else-if="isPending || fetchingSettings" />

    <NoPrioritisedPages v-else />
  </div>
</template>
